import React, { useState } from 'react';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import * as Sentry from '@sentry/react';
import { toggleSpinner } from 'apps/auth/actions/appAction';
import { addExtra } from 'apps/auth/actions/extraAction';
import { changeFormFieldValue, updateFormProperties } from 'apps/auth/actions/formAction';
import { NotFoundResult } from 'apps/auth/components/common/search/NotFoundResult';
import { Text } from 'apps/auth/components/common/Text/Text';
import { fetchData } from 'apps/auth/helpers/api';
import { usePageMessages } from 'apps/auth/hooks/selectors';

import Import from './Import';
import List from './List';

const phoneToNumber = (num) => Number(num.toString().replace(/[^\d]/g, ''));

const PhoneContacts = ({ submitRef, formName, action, toCountryCode, toCurrencyCode, url, onContactPick = null, ...props }) => {
    const [isNotFound, setNotFound] = useState();

    const { importDescription, phoneBookTitle, contactsAreNotFound } = usePageMessages();

    const select = async (phoneNums) => {
        if (onContactPick) {
            // Only one contact is available in order to create a request.
            // It'd be nice to notify a user about such behaviour.
            if (phoneNums.length) {
                onContactPick(phoneNums[0]);
                return;
            }

            await props.addExtra('contacts', []);
            return;
        }
        const list = phoneNums.map(phoneToNumber);
        if (!!list && !!list.length) {
            props.toggleSpinner(true);
            try {
                const response = await fetchData(`${url}/${toCountryCode}/${toCurrencyCode}/phone?list=${list.join(',')}`, 'GET');
                if (response?.extra?.contacts) {
                    await props.addExtra('contacts', []);
                    await props.addExtra('contacts', response.extra.contacts);
                    setNotFound(false);
                } else {
                    await props.addExtra('contacts', []);
                    setNotFound(true);
                }
            } catch (e) {
                Sentry.captureException(e, {
                    level: 'log',
                });
                setNotFound(true);
            }
        } else {
            Sentry.captureException(new Error('PhoneContacts: empty phone list'), {
                level: 'log',
            });
            await props.addExtra('contacts', []);
            setNotFound(true);
        }
        props.toggleSpinner(false);
    };

    const chooseRecipient = (phone) => {
        const value = phoneToNumber(phone).toString();
        if (value) {
            Promise.all([props.updateFormProperties(formName, action, false), props.changeFormFieldValue(formName, value, 'phone')]).then(() => {
                submitRef && submitRef.current && submitRef.current.click();
            });
        }
    };

    const isAdded = () => !!props.contacts && !!props.contacts.length && !isNotFound;

    const getContacts = () =>
        props.contacts.map(({ attributes }) => ({
            fullName: `${attributes['first-name']} ${attributes['last-name']}`,
            avatarUrl: attributes['avatar']['url'],
            phoneNum: attributes['phone'],
        }));

    return (
        <>
            <Text variant="div" size="subheader" color="secondary" indent="adaptive-32-48">
                {phoneBookTitle}
            </Text>
            {isNotFound && (
                <>
                    <NotFoundResult notFoundMessage={contactsAreNotFound} />
                    <Import onSelect={select} />
                </>
            )}
            {!isAdded() && (
                <>
                    <Text variant="div" indent="adaptive-16-24">
                        {importDescription}
                    </Text>
                    <Import onSelect={select} />
                </>
            )}
            {isAdded() && <List onImport={select} contacts={getContacts()} onClick={chooseRecipient} />}
        </>
    );
};

PhoneContacts.propTypes = {
    toCountryCode: PropTypes.string,
    toCurrencyCode: PropTypes.string,
    action: PropTypes.string,
};

const mapStateToProps = ({ extra, form }, ownProps) => ({
    fields: form[ownProps.formName].fields,
    phoneNum: form[ownProps.formName].fields.phone.value,
    toCountryCode: ownProps.toCountryCode,
    toCurrencyCode: ownProps.toCurrencyCode,
    contacts: extra.contacts,
    formName: ownProps.formName,
});

export default connect(mapStateToProps, { toggleSpinner, addExtra, changeFormFieldValue, updateFormProperties })(PhoneContacts);
